// src/components/queries/QueryFields.js
// Form parts of a query ("Abfrage"): the question with its answers or scale,
// and how often Steffi asks again when nobody answered. The limits mirror the
// backend's validation (app/services/queries.py).
import React from "react";

export const MIN_OPTIONS = 2;
export const MAX_OPTIONS = 6;
export const MAX_SCALE_STEPS = 11;
export const CUSTOM_QUERY_TEMPLATE = "custom_query";

const RETRY_MINUTES = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240];
const DEFAULT_RETRY = { every_minutes: 30, max_attempts: 3 };

function cls(...a) {
  return a.filter(Boolean).join(" ");
}

function minutesLabel(minutes) {
  if (minutes < 60) return `${minutes} Minuten`;
  const hours = minutes / 60;
  return `${String(hours).replace(".", ",")} ${hours === 1 ? "Stunde" : "Stunden"}`;
}

/** Form state for a query, from a template's `query` block or a saved `query_config`. */
export function queryFormState(source = {}) {
  return {
    question: source.question || "",
    answer_type: source.answer_type || "choice",
    options: Array.isArray(source.options) && source.options.length ? [...source.options] : ["", ""],
    scale: { min: 0, max: 10, min_label: "", max_label: "", ...(source.scale || {}) },
    retry: { ...DEFAULT_RETRY, ...(source.retry || {}) },
  };
}

/** The `query_config` to send. A predefined query only sends its retry policy. */
export function buildQueryConfig(query, custom) {
  const retry = {
    every_minutes: Number(query.retry.every_minutes),
    max_attempts: Number(query.retry.max_attempts),
  };
  if (!custom) return { retry };
  const config = { question: query.question.trim(), answer_type: query.answer_type, retry };
  if (query.answer_type === "scale") {
    config.scale = { min: Number(query.scale.min), max: Number(query.scale.max) };
    for (const key of ["min_label", "max_label"]) {
      const label = (query.scale[key] || "").trim();
      if (label) config.scale[key] = label;
    }
  } else {
    config.options = query.options.map((option) => option.trim()).filter(Boolean);
  }
  return config;
}

export function validateQuery(query, custom) {
  if (!custom) return null;
  if (!query.question.trim()) return "Bitte eine Frage eingeben.";
  if (query.question.trim().length > 200) return "Die Frage darf höchstens 200 Zeichen lang sein.";
  if (query.answer_type === "scale") {
    const min = Number(query.scale.min);
    const max = Number(query.scale.max);
    if (!Number.isInteger(min) || !Number.isInteger(max)) return "Die Skala braucht ganze Zahlen.";
    if (min >= max) return "Der Anfang der Skala muss kleiner als das Ende sein.";
    if (max - min + 1 > MAX_SCALE_STEPS) return `Eine Skala hat höchstens ${MAX_SCALE_STEPS} Stufen.`;
    return null;
  }
  const options = query.options.map((option) => option.trim()).filter(Boolean);
  if (options.length < MIN_OPTIONS) return `Bitte mindestens ${MIN_OPTIONS} Antworten eingeben.`;
  if (options.some((option) => option.length > 60)) return "Eine Antwort darf höchstens 60 Zeichen lang sein.";
  if (new Set(options.map((option) => option.toLowerCase())).size !== options.length) {
    return "Jede Antwort darf nur einmal vorkommen.";
  }
  return null;
}

// Shortest time between two scheduled asks, in minutes, or null when the
// schedule has only one time a day.
function shortestGapMinutes(form) {
  if (form.kind === "one_off") return null;
  if (form.mode === "frequency") return Number(form.every_minutes) || null;
  const minutes = (form.times || [])
    .map((time) => {
      const [h, m] = String(time).split(":").map(Number);
      return h * 60 + m;
    })
    .sort((a, b) => a - b);
  if (minutes.length < 2) return null;
  let gap = 24 * 60 - minutes[minutes.length - 1] + minutes[0];
  for (let i = 1; i < minutes.length; i += 1) gap = Math.min(gap, minutes[i] - minutes[i - 1]);
  return gap;
}

/** Shown, not enforced: an ask ends when the next one is due, so late retries drop out. */
export function retryScheduleWarning(form) {
  const query = form.query;
  if (!query || Number(query.retry.max_attempts) <= 1) return null;
  const gap = shortestGapMinutes(form);
  const lastRetry = (Number(query.retry.max_attempts) - 1) * Number(query.retry.every_minutes);
  if (!gap || lastRetry < gap) return null;
  return (
    `Die nächste geplante Frage kommt schon nach ${minutesLabel(gap)}. ` +
    "Wiederholungen danach entfallen – wähle weniger Versuche oder einen kürzeren Abstand."
  );
}

const inputCls = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400";

export function QuestionFields({ query, custom, onChange }) {
  const update = (changes) => onChange({ ...query, ...changes });
  const isScale = query.answer_type === "scale";

  if (!custom) {
    const { min, max, min_label: minLabel, max_label: maxLabel } = query.scale;
    const scaleLabels = [minLabel && `${min} = ${minLabel}`, maxLabel && `${max} = ${maxLabel}`].filter(Boolean).join(", ");
    return (
      <div className="grid gap-2 text-sm">
        <div className="text-gray-600">Frage und Antworten sind in dieser Vorlage festgelegt.</div>
        <div className="rounded-md bg-gray-100 px-3 py-2 font-medium text-gray-800">{query.question}</div>
        <div className="flex flex-wrap gap-2">
          {isScale ? (
            <span className="rounded-full bg-blue-50 px-3 py-1 text-blue-800">
              Skala {min} bis {max}{scaleLabels ? ` (${scaleLabels})` : ""}
            </span>
          ) : (
            query.options.map((option) => (
              <span key={option} className="rounded-full bg-blue-50 px-3 py-1 text-blue-800">{option}</span>
            ))
          )}
        </div>
      </div>
    );
  }

  const setOption = (index, value) => update({ options: query.options.map((o, i) => (i === index ? value : o)) });

  return (
    <div className="grid gap-3">
      <div>
        <label className="block text-sm font-medium text-gray-700">Frage</label>
        <textarea
          rows={2}
          maxLength={200}
          value={query.question}
          onChange={(e) => update({ question: e.target.value })}
          placeholder="Wie hast du heute Nacht geschlafen?"
          className={inputCls}
        />
      </div>

      <div className="flex gap-4 text-sm">
        {[
          { value: "choice", label: "Eigene Antworten" },
          { value: "scale", label: "Skala" },
        ].map((type) => (
          <label key={type.value} className="inline-flex items-center gap-2">
            <input
              type="radio"
              checked={query.answer_type === type.value}
              onChange={() => update({ answer_type: type.value })}
            />
            {type.label}
          </label>
        ))}
      </div>

      {isScale ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Von</label>
            <input type="number" step={1} value={query.scale.min}
              onChange={(e) => update({ scale: { ...query.scale, min: e.target.value } })} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Bis</label>
            <input type="number" step={1} value={query.scale.max}
              onChange={(e) => update({ scale: { ...query.scale, max: e.target.value } })} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Bedeutung „Von“</label>
            <input maxLength={40} value={query.scale.min_label} placeholder="keine Schmerzen"
              onChange={(e) => update({ scale: { ...query.scale, min_label: e.target.value } })} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Bedeutung „Bis“</label>
            <input maxLength={40} value={query.scale.max_label} placeholder="sehr starke Schmerzen"
              onChange={(e) => update({ scale: { ...query.scale, max_label: e.target.value } })} className={inputCls} />
          </div>
        </div>
      ) : (
        <div className="grid gap-2">
          <label className="block text-sm font-medium text-gray-700">
            Antworten ({MIN_OPTIONS} bis {MAX_OPTIONS})
          </label>
          {query.options.map((option, index) => (
            <div key={index} className="flex gap-2">
              <input
                value={option}
                maxLength={60}
                onChange={(e) => setOption(index, e.target.value)}
                placeholder={`Antwort ${index + 1}`}
                className={inputCls}
              />
              <button
                type="button"
                disabled={query.options.length <= MIN_OPTIONS}
                onClick={() => update({ options: query.options.filter((_, i) => i !== index) })}
                className="rounded-md border border-gray-300 px-3 text-sm text-gray-600 hover:bg-white disabled:opacity-40"
                title="Antwort entfernen"
              >
                ✕
              </button>
            </div>
          ))}
          {query.options.length < MAX_OPTIONS && (
            <button
              type="button"
              onClick={() => update({ options: [...query.options, ""] })}
              className="justify-self-start rounded-md border border-blue-200 bg-white px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50"
            >
              + Antwort hinzufügen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function RetryFields({ query, onChange, warning }) {
  const retry = query.retry;
  const repeats = Number(retry.max_attempts) > 1;
  const setRetry = (changes) => onChange({ ...query, retry: { ...retry, ...changes } });

  return (
    <div className="grid gap-3 text-sm">
      <label className="inline-flex items-center gap-2 font-medium text-gray-700">
        <input
          type="checkbox"
          checked={repeats}
          onChange={(e) => setRetry({ max_attempts: e.target.checked ? 3 : 1 })}
        />
        Bei fehlender Antwort erneut fragen
      </label>
      {repeats && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="block font-medium text-gray-700">Erneut fragen nach</label>
            <select
              value={retry.every_minutes}
              onChange={(e) => setRetry({ every_minutes: Number(e.target.value) })}
              className={inputCls}
            >
              {[...new Set([...RETRY_MINUTES, Number(retry.every_minutes)])].sort((a, b) => a - b).map((minutes) => (
                <option key={minutes} value={minutes}>{minutesLabel(minutes)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-medium text-gray-700">Höchstens so oft fragen (inkl. erster Frage)</label>
            <select
              value={retry.max_attempts}
              onChange={(e) => setRetry({ max_attempts: Number(e.target.value) })}
              className={inputCls}
            >
              {[2, 3, 4, 5].map((count) => (
                <option key={count} value={count}>{count} Mal</option>
              ))}
            </select>
          </div>
        </div>
      )}
      <p className={cls("text-gray-600", warning && "rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900")}>
        {warning ||
          (repeats
            ? `Antwortet niemand, fragt Steffi nach ${minutesLabel(Number(retry.every_minutes))} noch einmal, ` +
              `insgesamt höchstens ${retry.max_attempts} Mal. Sagt der Bewohner „später“, gilt das nicht als Antwort.`
            : "Steffi fragt einmal. Ohne Antwort wird „Keine Antwort“ gespeichert.")}
      </p>
    </div>
  );
}
